// index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Auth0Context, Auth0Provider } from '@auth0/auth0-react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { ChakraProvider, defaultSystem } from '@chakra-ui/react'; 
import { ToastContainer } from 'react-toastify';
import { ColorModeProvider } from './components/ui/color-mode';

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
const auth0Audience = process.env.REACT_APP_AUTH0_AUDIENCE;
const hasPlaywrightAuth0Mock =
  typeof window !== 'undefined' &&
  window.__PLAYWRIGHT_AUTH0__ &&
  typeof window.__PLAYWRIGHT_AUTH0__ === 'object';

if (!hasPlaywrightAuth0Mock && (!auth0Domain || !auth0ClientId || !auth0Audience)) {
  console.error("Auth0 environment variables (REACT_APP_AUTH0_DOMAIN, REACT_APP_AUTH0_CLIENT_ID, REACT_APP_AUTH0_AUDIENCE) are missing!");
}

const getPlaywrightAuth0Mock = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const mock = window.__PLAYWRIGHT_AUTH0__;
  return mock && typeof mock === 'object' ? mock : null;
};

const MockAuth0Provider = ({ children }) => {
  const mock = React.useMemo(() => {
    const source = getPlaywrightAuth0Mock() || {};

    return {
      error: undefined,
      isAuthenticated: Boolean(source.isAuthenticated),
      isLoading: false,
      user: source.user || null,
      loginWithRedirect:
        source.loginWithRedirect ||
        (async () => undefined),
      logout:
        source.logout ||
        (() => undefined),
      getAccessTokenSilently:
        source.getAccessTokenSilently ||
        (async () => source.token || 'playwright-token'),
      getAccessTokenWithPopup:
        source.getAccessTokenWithPopup ||
        (async () => source.token || 'playwright-token'),
      getIdTokenClaims:
        source.getIdTokenClaims ||
        (async () => null),
      loginWithPopup:
        source.loginWithPopup ||
        (async () => undefined),
      handleRedirectCallback:
        source.handleRedirectCallback ||
        (async () => undefined),
    };
  }, []);

  return <Auth0Context.Provider value={mock}>{children}</Auth0Context.Provider>;
};

const Auth0ProviderWithNavigate = ({ children }) => {
  const navigate = useNavigate();
  const playwrightMock = getPlaywrightAuth0Mock();

  const onRedirectCallback = (appState) => {
    const target = appState?.returnTo || window.location.pathname;
    navigate(target, { replace: true });
  };

  if (playwrightMock) {
    return <MockAuth0Provider>{children}</MockAuth0Provider>;
  }

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: auth0Audience,
        scope: 'openid profile email offline_access'
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

const rootElement = document.getElementById('root');
if (rootElement?.dataset?.prerenderedHomepage === 'true') {
  rootElement.replaceChildren();
  delete rootElement.dataset.prerenderedHomepage;
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Auth0ProviderWithNavigate>
        <ChakraProvider value={defaultSystem}>
          <ColorModeProvider>
            <App />
          </ColorModeProvider>
          <ToastContainer />
        </ChakraProvider>
      </Auth0ProviderWithNavigate>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
